import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { LogOut, User, Zap, ChevronLeft, CreditCard, Home, Layers, History, Sparkles, BookOpen, UserCircle, Gift, Receipt, GraduationCap, MessageSquare, X } from 'lucide-react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import axios from 'axios';
import JobHistory from './JobHistory';
import HomePage from './HomePage';
import ModelGallery from './ModelGallery';
import ModelPlayground from './ModelPlayground';
import BonusTopup from './BonusTopup';
import TopupHistory from './TopupHistory';
import EffectsPage from './EffectsPage';
import BlogList from './BlogList';
import BlogPost from './BlogPost';
import Profile from './Profile';
import ReferralPage from './ReferralPage';
import Footer from './Footer';
import ChatPanel from './ChatPanel';
import { getStoredChatCollapsed, setStoredChatCollapsed, fetchChatSettings } from '../services/chatService';
import './Dashboard.css';

function formatTokenBalance(value) {
  const n = Number(value) || 0;
  if (Math.abs(n - Math.round(n)) < 0.001) return String(Math.round(n));
  return n.toFixed(2).replace(/\.?0+$/, '');
}

function Dashboard() {
  const { user, logout, fetchUser, updateBalance } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [jobs, setJobs] = useState([]);
  const [jobsLoading, setJobsLoading] = useState(true); // Состояние загрузки jobs
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [bonusBalance, setBonusBalance] = useState(0);
  const [selectedModel, setSelectedModel] = useState(null);
  const [selectedPresetId, setSelectedPresetId] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [chatOpen, setChatOpen] = useState(() => !getStoredChatCollapsed());
  const [chatEnabled, setChatEnabled] = useState(false);

  // Флаг для предотвращения множественных одновременных запросов
  const isFetchingJobsRef = useRef(false);

  // Получаем активную вкладку из URL
  const getActiveTabFromPath = () => {
    const path = location.pathname;
    if (path === '/' || path === '/home') return 'home';
    if (path.startsWith('/models')) return 'models';
    if (path === '/effects') return 'effects';
    if (path === '/blog' || path.startsWith('/blog/')) return 'blog';
    if (path === '/topup') return 'topup';
    if (path === '/payments') return 'payments';
    if (path === '/history') return 'history';
    if (path === '/profile') return 'profile';
    if (path === '/referral') return 'referral';
    if (path.startsWith('/presets/')) return 'preset';
    return 'home';
  };

  const activeTab = getActiveTabFromPath();

  const buildPresetShellModel = (presetId) => ({
    id: null,
    slug: '',
    model_id: '',
    display_name: '',
    description: '',
    category: 'preset',
    input_parameters: {},
    default_parameters: {},
    supports_prompt: false,
    supports_multiple_images: false,
    cost_in_bonuses: 0,
    estimated_time_seconds: 0,
    preset_id: presetId,
  });

  const setChatOpenPersist = (open) => {
    setChatOpen(open);
    setStoredChatCollapsed(!open);
  };

  useEffect(() => {
    let cancelled = false;
    fetchChatSettings()
      .then((settings) => {
        if (cancelled) return;
        setChatEnabled(!!settings.enabled);
        if (settings.balance != null) updateBalance(settings.balance);
      })
      .catch(() => {
        if (!cancelled) setChatEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user?.id, updateBalance]);

  // React Router не сбрасывает скролл автоматически — делаем это сами
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
    if (window.matchMedia('(max-width: 768px)').matches) {
      setChatOpenPersist(false);
    }
  }, [location.pathname]);

  const openPresetShell = (presetId) => {
    setSelectedPresetId(presetId);
    setSelectedModel(buildPresetShellModel(presetId));
  };

  // Проверяем, есть ли slug модели в URL или presetId
  useEffect(() => {
    const modelMatch = location.pathname.match(/\/models\/([^/]+)/);
    const presetMatch = location.pathname.match(/\/presets\/([^/]+)/);

    if (modelMatch) {
      const modelSlug = modelMatch[1];
      setSelectedPresetId(null);
      fetchModelBySlug(modelSlug);
    } else if (presetMatch) {
      const presetId = presetMatch[1];
      openPresetShell(presetId);
    } else {
      setSelectedModel(null);
      setSelectedPresetId(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, location.search]);

  const fetchModelBySlug = async (modelSlug) => {
    try {
      const response = await axios.get(`/api/ai-models/by-slug/${modelSlug}`);
      setSelectedModel(response.data);
    } catch (error) {
      console.error('Error fetching model:', error);
      toast.error('Модель не найдена');
      navigate('/models');
    }
  };

  // Баланс загружается автоматически из user через useEffect ниже
  // Не нужно делать отдельный запрос, так как AuthContext уже загрузил данные пользователя

  // Загружаем jobs только при переходе на страницу истории (инициирует JobHistory через onRefresh)
  // Здесь не вызываем fetchJobs — JobHistory при монтировании сам вызовет onRefresh
  // и список будет обновляться при каждом переходе на /history, в том числе повторном

  const fetchJobs = useCallback(async (skip = 0, limit = 20, append = false) => {
    // Защита от множественных одновременных запросов
    if (isFetchingJobsRef.current && !append) {
      console.warn('Jobs fetch already in progress, skipping duplicate request...');
      return [];
    }

    // Устанавливаем loading только при первой загрузке (не при append)
    if (!append) {
      setJobsLoading(true);
      isFetchingJobsRef.current = true;
    }

    try {
      const response = await axios.get('/api/services/jobs', {
        params: { skip, limit }
      });
      // Проверяем, что ответ не пустой
      if (response.data && Array.isArray(response.data)) {
        if (append) {
          // Добавляем к существующим jobs
          setJobs(prev => [...prev, ...response.data]);
        } else {
          // Заменяем все jobs (при первой загрузке или обновлении)
          setJobs(response.data);
        }
        return response.data;
      } else {
        console.warn('Empty or invalid response from /api/services/jobs');
        return [];
      }
    } catch (error) {
      console.error('Error fetching jobs:', error);
      // При 502 или других ошибках не обновляем jobs, чтобы не терять текущие данные
      if (error.response?.status === 502) {
        console.warn('Backend unavailable (502), keeping current jobs');
      }
      return [];
    } finally {
      // Сбрасываем loading после завершения запроса
      if (!append) {
        setJobsLoading(false);
        isFetchingJobsRef.current = false;
      }
    }
  }, []);

  // Мемоизированная функция для обновления jobs
  const handleRefreshJobs = useCallback(async () => {
    return await fetchJobs(0, 20, false);
  }, [fetchJobs]);

  // Функция для обновления баланса (используется для обновления после пополнения)
  const fetchBalance = useCallback(async () => {
    // Используем fetchUser из AuthContext для обновления данных пользователя
    // Это обновит user, а баланс обновится автоматически через useEffect ниже
    await fetchUser();
  }, [fetchUser]);

  // Обновляем баланс из user при его изменении
  useEffect(() => {
    if (user && user.bonus_balance !== undefined) {
      setBonusBalance(parseFloat(user.bonus_balance));
    }
  }, [user]);

  const handleLogout = () => {
    logout();
    toast.success('Вы вышли из системы');
    navigate('/login');
  };

  const mainTabs = [
    { id: 'home', label: 'Главная', icon: Home },
    { id: 'models', label: 'AI Модели', icon: Layers },
    { id: 'effects', label: 'Эффекты', icon: Sparkles },
    { id: 'blog', label: 'Блог', icon: BookOpen },
    { id: 'courses', label: 'Обучение/курсы', icon: GraduationCap },
    { id: 'topup', label: 'Купить подписку или токены', icon: CreditCard },
  ];

  const pinnedTabs = [
    { id: 'history', label: 'История генераций', icon: History },
    { id: 'payments', label: 'Платежи', icon: Receipt },
  ];


  const renderActiveTab = () => {
    // Если выбрана модель, показываем playground
    if (selectedModel) {
      // Определяем, откуда пришли - из истории, из галереи моделей или из пресета
      const urlParams = new URLSearchParams(location.search);
      const fromParam = urlParams.get('from');
      const presetParam = urlParams.get('preset');
      const cameFromHistory = fromParam === 'history';
      const cameFromPreset = Boolean(presetParam && presetParam.trim());

      let backPath = '/models';
      if (cameFromHistory) {
        backPath = '/history';
      } else if (location.pathname.startsWith('/presets/')) {
        backPath = '/effects';
      } else if (cameFromPreset) {
        backPath = `/presets/${encodeURIComponent(presetParam.trim())}`;
      }

      return (
        <ModelPlayground
          model={selectedModel}
          initialPresetId={selectedPresetId}
          onBack={() => navigate(backPath)}
          currentBalance={bonusBalance}
          onBalanceUpdate={fetchBalance}
        />
      );
    }

    switch (activeTab) {
      case 'home':
        return <HomePage currentBalance={bonusBalance} />;
      case 'models':
        // Если открыта страница конкретной модели (есть slug в URL), не показываем галерею
        // Модель уже загружается через fetchModelBySlug и будет показана в ModelPlayground выше
        const match = location.pathname.match(/\/models\/([^/]+)/);
        if (match) {
          // Страница модели - показываем пустой контент или загрузку, пока модель загружается
          return selectedModel ? null : (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
              <div>Загрузка модели...</div>
            </div>
          );
        }
        return <ModelGallery />;
      case 'topup':
        return <BonusTopup currentBalance={bonusBalance} onBalanceUpdate={fetchBalance} />;
      case 'payments':
        return <TopupHistory />;
      case 'history':
        return <JobHistory jobs={jobs} loading={jobsLoading} onRefresh={handleRefreshJobs} onLoadMore={fetchJobs} />;
      case 'effects':
        return <EffectsPage />;
      case 'blog':
        // Если это страница отдельной статьи, показываем BlogPost, иначе BlogList
        const blogMatch = location.pathname.match(/\/blog\/(.+)/);
        if (blogMatch) {
          return <BlogPost />;
        }
        return <BlogList />;
      case 'profile':
        return <Profile />;
      case 'referral':
        return <ReferralPage />;
      case 'preset':
        // Если открыта страница пресета, показываем загрузку, пока модель загружается
        return selectedModel ? null : (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
            <div>Загрузка пресета...</div>
          </div>
        );
      default:
        return <HomePage currentBalance={bonusBalance} />;
    }
  };

  return (
    <div className={`dashboard ${mobileMenuOpen ? 'menu-open' : ''} ${chatEnabled && chatOpen ? 'chat-open' : ''}`}>
      {/* Мобильный верхний бар */}
      <div className="mobile-header">
        <div className="mobile-brand" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
          <img src="/logo.png" alt="Pikly" className="logo-icon" />
          <span className="brand-text">PIKLY</span>
        </div>

        <div className="mobile-balance-wrap">
          <div className="mobile-balance" aria-label="Баланс">
            <Zap size={16} />
            <span className="mobile-balance-value">{formatTokenBalance(bonusBalance)}</span>
            <span className="mobile-balance-label">токенов</span>
          </div>
        </div>

        <div className="mobile-header-actions">
          {chatEnabled && (
            <button
              className={`mobile-chat-toggle ${chatOpen ? 'open' : ''}`}
              onClick={() => {
                setMobileMenuOpen(false);
                setChatOpenPersist(!chatOpen);
              }}
              aria-label="Чат"
            >
              {chatOpen ? <X size={20} /> : <MessageSquare size={20} />}
            </button>
          )}
          <button
            className={`mobile-menu-toggle ${mobileMenuOpen ? 'open' : ''}`}
            onClick={() => {
              setChatOpenPersist(false);
              setMobileMenuOpen((v) => !v);
            }}
            aria-label="Меню"
          >
            <span className="bar"></span>
            <span className="bar"></span>
            <span className="bar"></span>
          </button>
        </div>
      </div>

      <div className="dashboard-body">
        <nav className={`dashboard-nav ${sidebarCollapsed ? 'collapsed' : ''} ${mobileMenuOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="logo" onClick={() => navigate('/home')} style={{ cursor: 'pointer' }}>
              {!sidebarCollapsed && <img src="/logo.png" alt="Pikly" className="logo-icon" />}
              <h1>PIKLY</h1>
            </div>
            <button
              className="sidebar-toggle"
              onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            >
              <ChevronLeft size={20} />
            </button>
          </div>

          {/* Баланс в сайдбаре */}
          <div className="sidebar-balance">
            <div className="balance-content">
              <Zap size={20} />
              {!sidebarCollapsed && (
                <div className="balance-text">
                  <span className="balance-label">Баланс</span>
                  <span className="balance-value">{formatTokenBalance(bonusBalance)} <span>токенов</span></span>
                </div>
              )}
            </div>
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Меню</div>
            {mainTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.id}
                  to={`/${tab.id}`}
                  className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={(e) => {
                    // Cmd/Ctrl + клик - открываем в новой вкладке (для Mac и Windows)
                    if (e.metaKey || e.ctrlKey) {
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    } else {
                      setMobileMenuOpen(false);
                    }
                  }}
                  onAuxClick={(e) => {
                    // Средняя кнопка мыши (колесико) или Fn + клик на macOS - открываем в новой вкладке
                    if (e.button === 1) {
                      // Средняя кнопка мыши
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    } else if (e.button === 2) {
                      // Правая кнопка мыши (Fn + клик на macOS может эмулировать это)
                      // Открываем в новой вкладке вместо показа контекстного меню
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    }
                  }}
                  onMouseDown={(e) => {
                    // Средняя кнопка мыши (колесико) - открываем в новой вкладке (fallback)
                    if (e.button === 1) {
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    }
                  }}
                  onContextMenu={(e) => {
                    // На macOS Fn + клик на трекпаде может вызвать onContextMenu
                    // Открываем в новой вкладке вместо показа контекстного меню
                    // Это позволяет использовать Fn + клик для открытия в новой вкладке
                    e.preventDefault();
                    window.open(`/${tab.id}`, '_blank');
                  }}
                  title={sidebarCollapsed ? tab.label : ''}
                >
                  <Icon size={20} />
                  {!sidebarCollapsed && tab.label}
                </Link>
              );
            })}
          </div>

          <div className="nav-section">
            <div className="nav-section-title">Закрепленные</div>
            {pinnedTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <Link
                  key={tab.id}
                  to={`/${tab.id}`}
                  className={`nav-tab ${activeTab === tab.id ? 'active' : ''}`}
                  onClick={(e) => {
                    // Cmd/Ctrl + клик - открываем в новой вкладке (для Mac и Windows)
                    if (e.metaKey || e.ctrlKey) {
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    } else {
                      setMobileMenuOpen(false);
                    }
                  }}
                  onAuxClick={(e) => {
                    // Средняя кнопка мыши (колесико) или Fn + клик на macOS - открываем в новой вкладке
                    if (e.button === 1) {
                      // Средняя кнопка мыши
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    } else if (e.button === 2) {
                      // Правая кнопка мыши (Fn + клик на macOS может эмулировать это)
                      // Открываем в новой вкладке вместо показа контекстного меню
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    }
                  }}
                  onMouseDown={(e) => {
                    // Средняя кнопка мыши (колесико) - открываем в новой вкладке (fallback)
                    if (e.button === 1) {
                      e.preventDefault();
                      window.open(`/${tab.id}`, '_blank');
                    }
                  }}
                  onContextMenu={(e) => {
                    // На macOS Fn + клик на трекпаде может вызвать onContextMenu
                    // Открываем в новой вкладке вместо показа контекстного меню
                    // Это позволяет использовать Fn + клик для открытия в новой вкладке
                    e.preventDefault();
                    window.open(`/${tab.id}`, '_blank');
                  }}
                  title={sidebarCollapsed ? tab.label : ''}
                >
                  <Icon size={20} />
                  {!sidebarCollapsed && tab.label}
                </Link>
              );
            })}
          </div>

          {/* Профиль и выход */}
          <div className="nav-section nav-section-bottom">
            <Link
              to="/profile"
              className={`nav-tab ${activeTab === 'profile' ? 'active' : ''}`}
              title={sidebarCollapsed ? (user?.username ? `Профиль (${user.username})` : 'Профиль') : ''}
              onClick={(e) => {
                // Cmd/Ctrl + клик - открываем в новой вкладке (для Mac и Windows)
                if (e.metaKey || e.ctrlKey) {
                  e.preventDefault();
                  window.open('/profile', '_blank');
                } else {
                  setMobileMenuOpen(false);
                }
              }}
              onAuxClick={(e) => {
                // Средняя кнопка мыши (колесико) или Fn + клик на macOS - открываем в новой вкладке
                if (e.button === 1) {
                  e.preventDefault();
                  window.open('/profile', '_blank');
                } else if (e.button === 2) {
                  e.preventDefault();
                  window.open('/profile', '_blank');
                }
              }}
              onMouseDown={(e) => {
                // Средняя кнопка мыши (колесико) - открываем в новой вкладке (fallback)
                if (e.button === 1) {
                  e.preventDefault();
                  window.open('/profile', '_blank');
                }
              }}
              onContextMenu={(e) => {
                // На macOS Fn + клик на трекпаде может вызвать onContextMenu
                // Открываем в новой вкладке вместо показа контекстного меню
                e.preventDefault();
                window.open('/profile', '_blank');
              }}
            >
              <UserCircle size={20} />
              {!sidebarCollapsed && (user?.username ? `Профиль (${user.username})` : 'Профиль')}
            </Link>
            <Link
              to="/referral"
              className={`nav-tab ${activeTab === 'referral' ? 'active' : ''}`}
              title={sidebarCollapsed ? 'Реферальная программа' : ''}
              onClick={(e) => {
                // Cmd/Ctrl + клик - открываем в новой вкладке (для Mac и Windows)
                if (e.metaKey || e.ctrlKey) {
                  e.preventDefault();
                  window.open('/referral', '_blank');
                } else {
                  setMobileMenuOpen(false);
                }
              }}
              onAuxClick={(e) => {
                // Средняя кнопка мыши (колесико) или Fn + клик на macOS - открываем в новой вкладке
                if (e.button === 1) {
                  e.preventDefault();
                  window.open('/referral', '_blank');
                } else if (e.button === 2) {
                  e.preventDefault();
                  window.open('/referral', '_blank');
                }
              }}
              onMouseDown={(e) => {
                // Средняя кнопка мыши (колесико) - открываем в новой вкладке (fallback)
                if (e.button === 1) {
                  e.preventDefault();
                  window.open('/referral', '_blank');
                }
              }}
              onContextMenu={(e) => {
                // На macOS Fn + клик на трекпаде может вызвать onContextMenu
                // Открываем в новой вкладке вместо показа контекстного меню
                e.preventDefault();
                window.open('/referral', '_blank');
              }}
            >
              <Gift size={20} />
              {!sidebarCollapsed && 'Реферальная программа'}
            </Link>
            <button
              className="nav-tab logout-btn"
              onClick={handleLogout}
              title={sidebarCollapsed ? 'Выйти' : ''}
            >
              <LogOut size={20} />
              {!sidebarCollapsed && 'Выйти'}
            </button>
          </div>
        </nav>

        <div className="main-content">
          <main className="dashboard-main">
            {renderActiveTab()}
          </main>
        </div>
      </div>

      {chatEnabled && (
        <ChatPanel
          open={chatOpen}
          onOpenChange={setChatOpenPersist}
          onBalanceChange={updateBalance}
        />
      )}

      {/* Футер на всю ширину (включая под меню) */}
      <Footer />
    </div>
  );
}

export default Dashboard;
